/** Viewer-local hair simulation preference, shared by settings and creator previews. */
export const FT3D_HAIR_MOTION_STORAGE_KEY = 'ft3d.hairMotion.v1';
export type Ft3dHairMotion = 'on' | 'off';
const eventName = 'ft3d-hair-motion-change';
export function readFt3dHairMotion(): Ft3dHairMotion {
    try { return localStorage.getItem(FT3D_HAIR_MOTION_STORAGE_KEY) === 'off' ? 'off' : 'on'; }
    catch { return 'on'; }
}
export function writeFt3dHairMotion(value: Ft3dHairMotion): void {
    try { localStorage.setItem(FT3D_HAIR_MOTION_STORAGE_KEY, value); } catch { /* Current window still updates. */ }
    window.dispatchEvent(new CustomEvent(eventName, { detail: value }));
}
export function subscribeFt3dHairMotion(notify: (value: Ft3dHairMotion) => void): () => void {
    const changed = (event: Event) => notify((event as CustomEvent<Ft3dHairMotion>).detail);
    const stored = (event: StorageEvent) => {
        if (event.key === null || event.key === FT3D_HAIR_MOTION_STORAGE_KEY) notify(readFt3dHairMotion());
    };
    window.addEventListener(eventName, changed);
    window.addEventListener('storage', stored);
    notify(readFt3dHairMotion());
    return () => { window.removeEventListener(eventName, changed); window.removeEventListener('storage', stored); };
}

export function buildHairMotionSection(): { root: HTMLElement; sync: (value: Ft3dHairMotion) => void } {
    const root = document.createElement('section');
    root.className = 'ft3d-hud-prefs__group';
    root.innerHTML = '<h3>Hair motion</h3><p class="ft3d-hud-prefs__hint">Adds subtle movement to supported hairstyles. Turn off to reduce processing; hair keeps its static fit and material quality.</p>';
    const choices = document.createElement('div');
    choices.className = 'ft3d-hud-prefs__choices';
    choices.setAttribute('role', 'group'); choices.setAttribute('aria-label', 'Hair motion');
    root.appendChild(choices);
    const buttons = (['on', 'off'] as const).map(value => {
        const button = document.createElement('button'); button.type = 'button';
        button.className = 'ft3d-hud-prefs__choice'; button.textContent = value === 'on' ? 'On' : 'Off';
        button.dataset.ft3dHairMotionChoice = value;
        button.onclick = () => writeFt3dHairMotion(value); choices.appendChild(button);
        return {value, button};
    });
    const sync = (value: Ft3dHairMotion) => buttons.forEach(row => {
        row.button.setAttribute('aria-pressed', String(row.value === value));
        row.button.classList.toggle('is-active', row.value === value);
        row.button.style.outline = row.value === value ? '2px solid #c9a454' : '';
        row.button.style.fontWeight = row.value === value ? '700' : '';
    });
    sync(readFt3dHairMotion()); return {root, sync};
}
